import React, { FC, memo, useEffect, useMemo } from 'react';
import {
  useFocusedIds,
  useIsFocusActive,
} from '@components/MainStage/focusOn.store';
import { getListSearchState } from '@components/MainStage/listSearch.store';
import useStore from '@components/MainStage/store';
import { useIsChromeHidden } from '@components/MainStage/uiChrome.store';
import { SideMenu } from '@components/SideMenu';
import { NodesTypes } from '@constants/canvas/general';
import { subLayersNodeIdsByMode } from '@constants/canvas/layerNodes';
import { Modes } from '@constants/canvas/layers';
import MenuNodesProvider from '@context/MenuNodesProvider';
import { useProject } from '@context/Project/ProjectProvider';
import { matchesModelFilters } from '@context/ProjectFilters/matchesModelFilters';
import { useProjectFilters } from '@context/ProjectFilters/ProjectFiltersProvider';
import useProjectId from '@hooks/useProjectId';
import { Box } from '@mui/material';
import { checkScopeFilter } from '@utils/domains';
import AssetsBlock from '@views/Project/components/ListMenu/components/AssetsBlock/AssetsBlock';
import FocusOnChip from '@views/Project/components/ListMenu/components/FocusOnChip';
import { LayerModeMenus } from '@views/Project/components/ListMenu/components/LayerModeMenus';
import { ListMenuHeader } from '@views/Project/components/ListMenu/components/ListMenuHeader';

type Props = {
  open: boolean;
};

export const ListMenu: FC<Props> = memo(({ open }) => {
  const { nodes } = useStore();
  const projectId = useProjectId(true);

  const {
    isVersionsShown,
    toolbox: { domains, mode },
  } = useProject();

  const {
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    ciaFilters,
  } = useProjectFilters();

  const isFocusActive = useIsFocusActive();
  const focusedIds = useFocusedIds();
  const isChromeHidden = useIsChromeHidden();

  useEffect(() => {
    getListSearchState().reset();
  }, [mode, projectId]);

  useEffect(() => () => getListSearchState().reset(), []);

  const nodesProviderValue = useMemo(() => {
    const focusedSet = new Set(focusedIds);

    const modelFilters = {
      validationStatus,
      reviewStatus,
      operationalStatuses,
      operatingModelStatuses,
      partners,
      ciaFilters,
    };

    return {
      nodes: mode
        ? nodes.filter(
            (node) =>
              node.type === NodesTypes.CustomNode &&
              node.parentNode &&
              subLayersNodeIdsByMode[mode].includes(node.parentNode) &&
              (!isFocusActive || focusedSet.has(node.id)) &&
              (!domains.length ||
                checkScopeFilter(
                  domains,
                  node.data?.dto?.type,
                  node.data?.dto?.domainNodes,
                )) &&
              matchesModelFilters(node, modelFilters),
          )
        : [],
    };
  }, [
    nodes,
    mode,
    domains,
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    ciaFilters,
    isFocusActive,
    focusedIds,
  ]);

  return (
    <SideMenu
      side="left"
      open={open && !isChromeHidden}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        ...(isChromeHidden && { borderRight: 'none' }),
      }}
    >
      {mode === Modes.RiskManagement ? (
        <AssetsBlock />
      ) : (
        <ListMenuHeader count={nodesProviderValue.nodes.length} />
      )}

      <FocusOnChip />

      {!isVersionsShown && (
        <Box
          overflow="auto"
          flex={1}
          pb={4}
          sx={{ scrollbarGutter: 'stable both-edges' }}
        >
          <MenuNodesProvider value={nodesProviderValue}>
            <LayerModeMenus />
          </MenuNodesProvider>
        </Box>
      )}
    </SideMenu>
  );
});
